import React, { useState } from 'react';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { Modal, Header, Button, Icon } from 'semantic-ui-react';
import { FormattedMessage } from 'react-intl';
import { GET_MEMBERSHIPS_QUERY } from '../containers/queries';

const DELETE_MEMBERSHIP_MUTATION = gql`
    mutation deleteMembership($input: DeleteMembershipInput!) {
        deleteMembership(input: $input) {
            membershipId
        }
    }
`;

function DeleteMembershipDialog({ membership, trigger, open, onClose }) {
    const salon = useSelector(({ user }) => user.get('salon'));
    const currentLanguage = useSelector(
        ({ intl }) => intl.get('locale') || intl.get('defaultLanguage'),
    );
    const [deleteMembership, { loading }] = useMutation(DELETE_MEMBERSHIP_MUTATION);
    const [deleteFailed, setDeleteFailed] = useState(false);

    const onDelete = () => {
        setDeleteFailed(false);
        // No optimisticResponse: the row stays visible while the request is
        // in flight, so the modal can show an error if the server rejects it.
        // On success the cache update removes the row, naturally unmounting
        // this component — no need to call onClose() in .then().
        deleteMembership({
            variables: { input: { id: membership.id } },
            update: (store, { data: { deleteMembership: result } }) => {
                const data = store.readQuery({
                    query: GET_MEMBERSHIPS_QUERY,
                    variables: { salon, lang: currentLanguage },
                });
                store.writeQuery({
                    query: GET_MEMBERSHIPS_QUERY,
                    variables: { salon, lang: currentLanguage },
                    data: {
                        ...data,
                        viewer: {
                            ...data.viewer,
                            salon: {
                                ...data.viewer.salon,
                                memberships: data.viewer.salon.memberships.filter(
                                    m => m.id !== result.membershipId,
                                ),
                            },
                        },
                    },
                });
            },
        }).catch(() => {
            setDeleteFailed(true);
        });
    };

    return (
        <Modal trigger={trigger} open={open} onClose={onClose} size="small" basic>
            <Header icon>
                <Icon name="remove" />
                <FormattedMessage
                    id="pages.memberships.deleteMembership.header"
                    defaultMessage="Delete membership"
                />
            </Header>
            <Modal.Content>
                <FormattedMessage
                    id="pages.memberships.deleteMembership.text"
                    defaultMessage="Are you sure you want to delete membership {serialNumber}?"
                    values={{
                        serialNumber: membership.serialNumber,
                    }}
                />
                {deleteFailed && (
                    <p>
                        <FormattedMessage
                            id="errors.defaultMessage"
                            defaultMessage="Something went wrong"
                        />
                    </p>
                )}
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={onClose} color="red" basic inverted disabled={loading}>
                    <Icon name="remove" />
                    <FormattedMessage
                        id="pages.memberships.delete.no"
                        defaultMessage="No"
                    />
                </Button>
                <Button onClick={onDelete} color="green" inverted loading={loading} disabled={loading}>
                    <Icon name="checkmark" />
                    <FormattedMessage
                        id="pages.memberships.delete.yes"
                        defaultMessage="Yes"
                    />
                </Button>
            </Modal.Actions>
        </Modal>
    );
}

export default DeleteMembershipDialog;
